import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCategoryBudgets } from './useCategoryBudgets';
import { useTransactions } from './useTransactions';
import { useSubscription } from './useSubscription';

const categoryLabels: Record<string, string> = {
  food: 'Alimentación',
  transport: 'Transporte',
  entertainment: 'Entretenimiento',
  health: 'Salud',
  education: 'Educación',
  clothing: 'Ropa',
  personal_care: 'Cuidado personal',
  gifts: 'Regalos',
  subscriptions: 'Suscripciones',
  other: 'Otros',
  supermercado: 'Supermercado',
  restaurantes: 'Restaurantes',
  belleza: 'Belleza',
  mascotas: 'Mascotas',
  hogar: 'Hogar',
};

export function useBudgetEmailAlert() {
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const { budgets } = useCategoryBudgets();
  const { transactions } = useTransactions();

  const checkAndSendBudgetAlert = async (
    category: string,
    newAmount: number,
    currency: string
  ) => {
    if (!user || !budgets?.length || !isPremium) return;

    const now = new Date();
    const targetMonth = now.getMonth() + 1;
    const targetYear = now.getFullYear();

    // Find budget for this category
    const budget = budgets.find(
      b => b.category.toLowerCase() === category.toLowerCase() && b.is_active
    );

    if (!budget) return;

    // Calculate current spending for this category (before adding new transaction)
    const currentSpending = transactions
      ?.filter(t => {
        const txDate = new Date(t.date);
        return (
          t.category.toLowerCase() === category.toLowerCase() &&
          txDate.getMonth() + 1 === targetMonth &&
          txDate.getFullYear() === targetYear &&
          (t.type === 'variable_expense' || t.type === 'fixed_expense')
        );
      })
      .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

    const totalAfterNew = currentSpending + newAmount;
    const wasExceededBefore = currentSpending >= budget.monthly_limit;
    const isExceededAfter = totalAfterNew >= budget.monthly_limit;

    // Only send email if this transaction causes the budget to be exceeded for the first time
    if (!wasExceededBefore && isExceededAfter) {
      try {
        console.log(`Budget exceeded for ${category}, sending email alert...`);

        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) return;

        const { error } = await supabase.functions.invoke('send-budget-alert', {
          body: {
            user_id: user.id,
            category: category,
            spent: totalAfterNew,
            limit: budget.monthly_limit,
            currency: currency,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          console.error('Error sending budget alert email:', error);
        } else {
          console.log('Budget alert email sent successfully');
        }
      } catch (error) {
        console.error('Error invoking send-budget-alert function:', error);
      }
    }
  };

  return { checkAndSendBudgetAlert };
}
